import { observable, toJS } from 'mobx';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import type { Moment } from '../lib/moment';
import type OrderItem from './order_item2';
import type {
  HotelElementGuestArgs,
  HotelElementGuestResponseArgs
} from './hotel_element_guest/hotel_element_guest2';
import HotelElementGuest, {
  convertHotelElementGuestResponseToArgs
} from './hotel_element_guest/hotel_element_guest2';
import type ElementDetailOptions from './element_detail_options';
import type { RakutenTravelRConnectBookingInfoResponseArgs } from './rakuten_travel_r_connect_booking_info';
import RakutenTravelRConnectBookingInfo, {
  convertRakutenTravelRConnectBookingInfoResponseToArgs
} from './rakuten_travel_r_connect_booking_info';
import type { CancelLimitResponseArgs } from './cancel_limit2';
import CancelLimit, { convertCancelLimitResponseToArgs } from './cancel_limit2';
import { resolveDisplayCancelPolicy } from './cancel_policy_formatter';
import type { HotelReservationResponseArgs } from './hotel_reservation2';
import HotelReservation, { convertHotelReservationResponseToArgs } from './hotel_reservation2';

export const HOTEL_PLAN_POINT_NOTICE =
  '※ポイント等の特典付きの表示がある場合がございますが、付与対象になりませんのでご了承ください';

export type HotelElementResponseArgs = {
  id: number;
  name: string;
  room_num: number;
  checkin_date: string;
  checkout_date: string;
  provider: string;
  plan_name: string;
  room_type: string;
  breakfast?: boolean;
  restricted: boolean;
  too_late: boolean;
  hotel_element_guests?: HotelElementGuestResponseArgs[];
  cancel_limits?: CancelLimitResponseArgs[];
  package_type?: string;
  marketlog_available?: boolean;
  reserve_url?: string;
  rakuten_travel_r_connect_booking_infos?: RakutenTravelRConnectBookingInfoResponseArgs[];
  raw_json?: string;
  hotel_reservation_number?: string;
  address?: string;
  tel?: string;
  is_foreign?: boolean | null;
  guest_first_name?: string | null;
  guest_last_name?: string | null;
  guest_first_name_kana?: string | null;
  guest_last_name_kana?: string | null;
  guest_tel?: string | null;
  hotel_reservation?: HotelReservationResponseArgs;
  refundable?: boolean;
  bed_type?: string;
  bed_type_notice_message?: string;
  people_num?: number;
  latitude?: number;
  longitude?: number;
  checkin_time?: string;
  checkout_time?: string;
  cancel_policy?: string;
};

export type HotelElementArgs = {
  id: number;
  name: string;
  roomNum: number;
  checkinDate: Moment;
  checkoutDate: Moment;
  provider: string;
  providerOptions?: { [key: string]: string };
  planName: string;
  roomType: string;
  breakfast?: boolean;
  restricted: boolean;
  tooLate: boolean;
  hotelElementGuests?: HotelElementGuestArgs[];
  packageType?: string;
  marketlogAvailable?: boolean;
  detailPath?: string;
  reserveUrl?: string;
  rakutenTravelRConnectBookingInfos?: RakutenTravelRConnectBookingInfo[];
  rawJson?: string;
  hotelReservationNumber?: string;
  address?: string;
  tel?: string;
  isForeign?: boolean | null;
  guestFirstName?: string;
  guestLastName?: string;
  guestFirstNameKana?: string;
  guestLastNameKana?: string;
  guestTel?: string;
  hotelReservation?: HotelReservation;
  cancelLimits?: CancelLimit[];
  refundable?: boolean;
  bedType?: string;
  bedTypeNoticeMessage?: string;
  peopleNum?: number;
  latitude?: number;
  longitude?: number;
  checkinTime?: string;
  checkoutTime?: string;
  cancelPolicy?: string;
};

export const convertHotelElementResponseToArgs = (
  response: HotelElementResponseArgs,
  providerOptions?: { [key: string]: string }
) => {
  const args: HotelElementArgs = {
    id: response.id,
    name: response.name,
    roomNum: response.room_num,
    checkinDate: moment(response.checkin_date),
    checkoutDate: moment(response.checkout_date),
    provider: response.provider,
    planName: response.plan_name,
    roomType: response.room_type,
    breakfast: response.breakfast,
    restricted: response.restricted,
    tooLate: response.too_late,
    hotelElementGuests: response.hotel_element_guests?.map(convertHotelElementGuestResponseToArgs),
    packageType: response.package_type,
    marketlogAvailable: response.marketlog_available,
    reserveUrl: response.reserve_url,
    rakutenTravelRConnectBookingInfos: response.rakuten_travel_r_connect_booking_infos?.map(
      r => new RakutenTravelRConnectBookingInfo(convertRakutenTravelRConnectBookingInfoResponseToArgs(r))
    ),
    rawJson: response.raw_json,
    hotelReservationNumber: response.hotel_reservation_number,
    address: response.address,
    tel: response.tel,
    isForeign: response.is_foreign,
    guestFirstName: response.guest_first_name ?? undefined,
    guestLastName: response.guest_last_name ?? undefined,
    guestFirstNameKana: response.guest_first_name_kana ?? undefined,
    guestLastNameKana: response.guest_last_name_kana ?? undefined,
    guestTel: response.guest_tel ?? undefined,
    peopleNum: response.people_num,
    hotelReservation: response.hotel_reservation
      ? new HotelReservation(convertHotelReservationResponseToArgs(response.hotel_reservation))
      : undefined,
    cancelLimits: response.cancel_limits?.map(limit => new CancelLimit(convertCancelLimitResponseToArgs(limit))),
    refundable: response.refundable,
    bedType: response.bed_type,
    bedTypeNoticeMessage: response.bed_type_notice_message,
    latitude: response.latitude,
    longitude: response.longitude,
    checkinTime: response.checkin_time,
    checkoutTime: response.checkout_time,
    cancelPolicy: response.cancel_policy,
    providerOptions
  };
  return args;
};

export default class HotelElement {
  @observable id: number;
  @observable name: string;
  @observable roomNum: number;
  @observable checkinDate: Moment;
  @observable checkoutDate: Moment;
  @observable provider: string;
  @observable providerOptions?: { [key: string]: string };
  @observable planName: string;
  @observable roomType: string;
  @observable breakfast?: boolean;
  @observable restricted: boolean;
  @observable tooLate: boolean;
  @observable hotelElementGuests: HotelElementGuest[] = [];
  @observable packageType?: string;
  @observable marketlogAvailable?: boolean;
  @observable detailPath?: string;
  @observable reserveUrl?: string;
  @observable rakutenTravelRConnectBookingInfos?: RakutenTravelRConnectBookingInfo[];
  @observable rawJson?: string;
  @observable hotelReservationNumber?: string;
  @observable address?: string;
  @observable tel?: string;
  @observable isForeign?: boolean | null;
  @observable hotelReservation?: HotelReservation;
  @observable peopleNum?: number;
  @observable bedType?: string;
  @observable refundable?: boolean;
  @observable bedTypeNoticeMessage?: string;
  @observable guestLastName?: string;
  @observable guestFirstName?: string;
  @observable guestLastNameKana?: string;
  @observable guestFirstNameKana?: string;
  @observable guestTel?: string;
  @observable rawId?: string;
  @observable eanItineraryId?: string;
  @observable rapidItineraryId?: string;
  @observable eanConfirmationNumber?: string;
  @observable eanAffiliateConfirmationId?: string;
  @observable apiCanceledAt?: Moment;
  @observable cancelLimits: CancelLimit[];
  @observable latitude?: number;
  @observable longitude?: number;
  @observable checkinTime?: string;
  @observable checkoutTime?: string;
  @observable initialData?: HotelElement;
  @observable cancelPolicy?: string;

  constructor(args: HotelElementArgs) {
    this.id = args.id;
    this.name = args.name;
    this.roomNum = args.roomNum;
    this.checkinDate = args.checkinDate;
    this.checkoutDate = args.checkoutDate;
    this.provider = args.provider;
    this.providerOptions = args.providerOptions;
    this.planName = args.planName;
    this.roomType = args.roomType;
    this.breakfast = args.breakfast;
    this.restricted = args.restricted;
    this.tooLate = args.tooLate;
    this.hotelElementGuests = args.hotelElementGuests?.map(guest => new HotelElementGuest(guest)) || [];
    this.packageType = args.packageType;
    this.marketlogAvailable = args.marketlogAvailable;
    this.reserveUrl = args.reserveUrl;
    this.rakutenTravelRConnectBookingInfos = args.rakutenTravelRConnectBookingInfos;
    this.rawJson = args.rawJson;
    this.hotelReservationNumber = args.hotelReservationNumber;
    this.address = args.address;
    this.tel = args.tel;
    this.isForeign = args.isForeign;
    this.guestFirstName = args.guestFirstName;
    this.guestLastName = args.guestLastName;
    this.guestFirstNameKana = args.guestFirstNameKana;
    this.guestLastNameKana = args.guestLastNameKana;
    this.guestTel = args.guestTel;
    this.hotelReservation = args.hotelReservation;
    this.cancelLimits = args.cancelLimits || [];
    this.refundable = args.refundable;
    this.bedType = args.bedType;
    this.bedTypeNoticeMessage = args.bedTypeNoticeMessage;
    this.peopleNum = args.peopleNum;
    this.latitude = args.latitude;
    this.longitude = args.longitude;
    this.checkinTime = args.checkinTime;
    this.checkoutTime = args.checkoutTime;
    this.cancelPolicy = args.cancelPolicy;
    if ((!this.provider || this.provider === '') && this.providerOptions) {
      const [defaultProvider] = Object.keys(this.providerOptions);
      if (defaultProvider) {
        this.provider = defaultProvider;
      }
    }
    this.initialData = cloneDeep(toJS(this));
  }

  // 海外ホテルフラグを更新
  handleIsForeignChange(value: boolean): void {
    this.isForeign = value;
  }

  get type() {
    return 'hotel';
  }

  get summary() {
    return `${this.checkinDate.format('YYYY-MM-DD')} 〜 ${this.checkoutDate.format('YYYY-MM-DD')}`;
  }

  get title() {
    return '宿泊';
  }

  get breakfastDescription() {
    let d = '\n朝食：';
    switch (this.breakfast) {
      case true:
        d += '有り';
        break;
      case false:
        d += '無し';
        break;
      default:
        d += 'お問合せください';
        break;
    }
    return d;
  }

  get marketLogType() {
    return this.type;
  }

  get isPackage() {
    return !!this.packageType || this.provider === 'wbf';
  }

  detail(orderItem: OrderItem, opts: ElementDetailOptions = {}) {
    return this.structuredDetail(orderItem, opts)
      .map(line => `${line.label}${line.value}\n`)
      .join('');
  }

  structuredDetail(_orderItem: OrderItem, opts: ElementDetailOptions = {}) {
    const lines = this.structuredHotelTypeDetail(opts);
    lines.push(...this.structuredRoomTypeDetail());

    return lines;
  }

  structuredHotelTypeDetail(opts: ElementDetailOptions = {}) {
    const lines = [];
    lines.push({
      label: opts.inlineRestricted && this.restricted ? 'RESTRICTED!宿泊プラン：' : '宿泊プラン：',
      value: this.planName ? this.planName : '-',
      translate: true,
      kind: 'plan_name',
      restricted: this.restricted
    });

    if (this.provider !== 'agoda') {
      lines.push({
        label: '部屋タイプ：',
        value: this.roomType ? this.roomType : '',
        translate: true
      });
    }
    lines.push({
      label: '',
      value: this.breakfastDescription,
      translate: true
    });
    return lines;
  }

  get staySchedule(): string {
    return `${this.checkinDate.format('YYYY-MM-DD')} 〜 ${this.checkoutDate.format('YYYY-MM-DD')}`;
  }

  get stayDays(): number {
    return this.checkoutDate.diff(this.checkinDate, 'days');
  }

  get planNameDescription(): string {
    if (!this.planName || this.planName === '') {
      return '宿泊プラン：-';
    }
    return `\n宿泊プラン：${this.planName}`;
  }

  get roomTypeDescription(): string {
    if (!this.roomType || this.roomType === '') {
      return '';
    }
    return `\n部屋タイプ：${this.roomType}`;
  }

  get bedTypeDescription(): string {
    const displayType = this.bedType || '';
    if (!displayType || displayType === '') {
      return '';
    }
    if (this.bedTypeNoticeMessage) {
      return `\nベッドタイプ：${displayType}\n${this.bedTypeNoticeMessage}`;
    }
    return `\nベッドタイプ：${displayType}`;
  }

  get refundableDescription(): string {
    if (this.isPackage) {
      return '';
    }
    let d = '\n返金：';
    switch (this.refundable) {
      case true:
        d += '可';
        break;
      case false:
        d += '不可';
        break;
      default:
        d += 'お問合せください';
        break;
    }
    return d;
  }

  get addressDescription(): string {
    if (!this.address || this.address === '') {
      return '';
    }
    return `\n■住所\n${this.address}`;
  }

  get telDescription(): string {
    if (!this.tel || this.tel === '') {
      return '';
    }
    return `\n■電話番号\n${this.tel}`;
  }

  get hotelReservationNumberDescription(): string {
    if (!this.hotelReservationNumber || this.hotelReservationNumber === '') {
      return '';
    }
    return `\n■ホテル予約番号\n${this.hotelReservationNumber}`;
  }

  get description(): string {
    let d = `<宿泊>
■ご利用：${this.peopleNum}名${this.roomNum}室
■日程
${this.staySchedule}
${this.stayDays}泊
■ホテル
${this.name}`;

    d +=
      this.planNameDescription +
      this.roomTypeDescription +
      this.bedTypeDescription +
      this.breakfastDescription +
      this.refundableDescription +
      this.addressDescription +
      this.telDescription +
      this.hotelReservationNumberDescription;

    // 各部屋の情報を追加
    d += this.hotelElementGuests
      .map((guest, i) => {
        return `


<${i + 1}室目>
${guest.bedTypeDescriptionOverview}
■宿泊代表者
${guest.guestLastName} ${guest.guestFirstName}
${guest.guestTel}`;
      })
      .join('');

    return d;
  }

  structuredRoomTypeDetail() {
    const lines: Array<{ label: string; value: string; translate: boolean }> = [];
    this.hotelElementGuests.forEach((guest, i) => {
      lines.push(
        {
          label: '',
          value: `<${i + 1}室目>`,
          translate: false
        },
        {
          label: '',
          value: guest.bedTypeDescriptionOverview,
          translate: false
        },
        {
          label: '',
          value: `${guest.guestLastName} ${guest.guestFirstName} (${guest.guestTel})`,
          translate: false
        }
      );
    });
    return lines;
  }

  addCancelLimit(limit: CancelLimit) {
    this.cancelLimits.push(limit);
  }

  removeCancelLimit(limit: CancelLimit) {
    this.cancelLimits = this.cancelLimits.filter(l => l !== limit);
  }

  validationErrors(orderItemStatus?: number): string[] {
    const errors: string[] = [];
    const needStatusGuard = orderItemStatus === 7 || orderItemStatus === 13;

    if (!this.name || this.name === '') {
      errors.push('ホテル名を入力してください。\n予約が不要な場合、\nホテルの内容を削除してください。');
    }

    if (this.checkoutDate && this.checkinDate && this.checkoutDate < this.checkinDate) {
      errors.push('チェックイン以降を指定してください。');
    }

    if (needStatusGuard && (!this.hotelReservationNumber || this.hotelReservationNumber === '')) {
      errors.push('予約番号を入力してください。');
    }

    // Add validation errors from cancel limits
    this.cancelLimits.forEach(limit => {
      errors.push(...limit.validationErrors());
    });

    return errors;
  }

  restore(): void {
    if (!this.initialData) return;

    this.name = this.initialData.name;
    this.roomNum = this.initialData.roomNum;
    this.checkinDate = this.initialData.checkinDate;
    this.checkoutDate = this.initialData.checkoutDate;
    this.provider = this.initialData.provider;
    this.planName = this.initialData.planName;
    this.roomType = this.initialData.roomType;
    this.breakfast = this.initialData.breakfast;
    this.restricted = this.initialData.restricted;
    this.tooLate = this.initialData.tooLate;
    this.hotelElementGuests = this.initialData.hotelElementGuests;
    this.packageType = this.initialData.packageType;
    this.marketlogAvailable = this.initialData.marketlogAvailable;
    this.detailPath = this.initialData.detailPath;
    this.reserveUrl = this.initialData.reserveUrl;
    this.rakutenTravelRConnectBookingInfos = this.initialData.rakutenTravelRConnectBookingInfos;
    this.rawJson = this.initialData.rawJson;
    this.hotelReservationNumber = this.initialData.hotelReservationNumber;
    this.address = this.initialData.address;
    this.tel = this.initialData.tel;
    this.isForeign = this.initialData.isForeign;
    this.hotelReservation = this.initialData.hotelReservation;
    this.peopleNum = this.initialData.peopleNum;
    this.bedType = this.initialData.bedType;
    this.refundable = this.initialData.refundable;
    this.bedTypeNoticeMessage = this.initialData.bedTypeNoticeMessage;
    this.guestLastName = this.initialData.guestLastName;
    this.guestFirstName = this.initialData.guestFirstName;
    this.guestLastNameKana = this.initialData.guestLastNameKana;
    this.guestFirstNameKana = this.initialData.guestFirstNameKana;
    this.guestTel = this.initialData.guestTel;
    this.rawId = this.initialData.rawId;
    this.eanItineraryId = this.initialData.eanItineraryId;
    this.rapidItineraryId = this.initialData.rapidItineraryId;
    this.eanConfirmationNumber = this.initialData.eanConfirmationNumber;
    this.eanAffiliateConfirmationId = this.initialData.eanAffiliateConfirmationId;
    this.apiCanceledAt = this.initialData.apiCanceledAt;
    this.cancelLimits = this.initialData.cancelLimits;
  }

  get updateParams() {
    return {
      type: this.type,
      checkin_date: this.checkinDate?.format('YYYY-MM-DD'),
      checkout_date: this.checkoutDate?.format('YYYY-MM-DD'),
      name: this.name,
      provider: this.provider,
      people_num: this.peopleNum,
      room_num: this.roomNum,
      plan_name: this.planName,
      room_type: this.roomType,
      bed_type: this.bedType,
      address: this.address,
      tel: this.tel,
      breakfast: this.breakfast === true ? true : this.breakfast === false ? false : null,
      refundable: this.refundable === true ? true : this.refundable === false ? false : null,
      guest_last_name: this.guestLastName,
      guest_first_name: this.guestFirstName,
      guest_last_name_kana: this.guestLastNameKana,
      guest_first_name_kana: this.guestFirstNameKana,
      guest_tel: this.guestTel,
      raw_id: this.rawId,
      ean_itinerary_id: this.eanItineraryId,
      rapid_itinerary_id: this.rapidItineraryId,
      ean_confirmation_number: this.eanConfirmationNumber,
      ean_affiliate_confirmation_id: this.eanAffiliateConfirmationId,
      api_canceled_at: this.apiCanceledAt?.format(),
      hotel_reservation: this.hotelReservation,
      hotel_reservation_number: this.hotelReservationNumber,
      is_foreign: this.isForeign,
      rakuten_hotel_reservation_ids:
        this.rakutenTravelRConnectBookingInfos?.map(info => info.hotelReservationId) || [],
      cancel_limits_attributes: this.cancelLimits.map(limit => limit.updateParams)
    };
  }

  get isCanceled() {
    return this.provider === 'mynavi' && this.apiCanceledAt !== null;
  }

  get mappingDescription(): string {
    return `ホテル：${this.name}`;
  }

  get hotelMapAddressLink() {
    if (this.address && this.address !== '') {
      return `https://www.google.com/maps/search/${this.address}`;
    }
    if (this.latitude && this.longitude) {
      return `https://www.google.com/maps/search/${this.latitude}, ${this.longitude}`;
    }
    return '';
  }

  get startTime() {
    return this.checkinDate;
  }

  get endTime() {
    return this.checkoutDate;
  }

  get hotelTypeDetail() {
    return this.structuredHotelTypeDetail()
      .map(line => `${line.label || ''}${line.value || ''}\n`)
      .join('');
  }

  get roomTypeDetail() {
    return this.structuredRoomTypeDetail()
      .map(line => `${line.label || ''}${line.value || ''}\n`)
      .join('');
  }

  get displayCancelPolicy(): string | null {
    return resolveDisplayCancelPolicy({
      cancelLimits: this.cancelLimits,
      cancelPolicy: this.cancelPolicy,
      checkinDate: this.checkinDate
    });
  }
}
